/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Root Application Shell for Admin Backoffice Portal with HTML5 Routing (Task 2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useActiveAdminTemplate } from './hooks/useActiveAdminTemplate';
import { useAdminRouting } from './hooks/useAdminRouting';
import { isValidAdminPath } from './routes/admin-routes';
import { AdminDashboardView } from './views/AdminDashboardView';
import { AdminAuditLogsView } from './views/AdminAuditLogsView';
import { AiModelsManagementView } from './views/AiModelsManagementView';
import { AiSandboxView } from './views/AiSandboxView';
import { PlansManagementPage } from './views/PlansManagementPage';
import { KeyPoolMonitorView } from './views/KeyPoolMonitorView';
import { PaymentGatewaysConfigView } from './views/PaymentGatewaysConfigView';
import { BankTransfersApprovalView } from './views/BankTransfersApprovalView';
import { PaymentOrdersLogView } from './views/PaymentOrdersLogView';
import { FinancialManagementView } from './views/FinancialManagementView';
import { AdminCreditsManagementView } from './views/AdminCreditsManagementView';
import { UserDirectoryView } from './views/UserDirectoryView';
import { CmsBlogManagementView } from './views/CmsBlogManagementView';
import { CustomPagesManagementView } from './views/CustomPagesManagementView';
import { NavigationMenuManagementView } from './views/NavigationMenuManagementView';
import { SystemSettingsView } from './views/SystemSettingsView';
import { ThemeStudioView } from './views/ThemeStudioView';
import { TemplateStudioView } from './views/TemplateStudioView';
import { BrandStudioView } from './views/BrandStudioView';
import { SeedAssetsView } from './views/SeedAssetsView';
import { TokenAnalyticsView } from './views/TokenAnalyticsView';
import { AdminLoginView } from './views/AdminLoginView';
import { useAdminAuthStore } from './stores/useAdminAuthStore';
import { useI18n } from './i18n';
import { AlertCircle, ArrowLeft, Wrench } from 'lucide-react';

export const App: React.FC = () => {
  const { t } = useI18n();
  const { isAuthenticated } = useAdminAuthStore();
  const { LayoutComponent } = useActiveAdminTemplate();
  const { activeView, currentPath, navigateTo, setActiveView } = useAdminRouting();
  const [sandboxModelCode, setSandboxModelCode] = useState<string | undefined>(undefined);

  if (!isAuthenticated) {
    return <AdminLoginView />;
  }

  const navigateToSandboxWithModel = (modelCode: string) => {
    setSandboxModelCode(modelCode);
    navigateTo('aiSandbox');
  };

  const renderView = () => {
    // 1. Check for invalid path (404 Fallback)
    if (!isValidAdminPath(currentPath)) {
      return (
        <div className="p-12 text-center border border-dashed border-surface-border rounded-2xl bg-surface space-y-4 max-w-lg mx-auto mt-10">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 mx-auto">
            <AlertCircle className="w-7 h-7" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-heading font-bold text-foreground">
              {t('common.notFound' as any, undefined, 'Page Not Found')}
            </h3>
            <p className="text-xs text-muted-fg leading-relaxed">
              {t('admin.routing.notFoundDesc' as any, undefined, 'The requested backoffice path does not exist.')}
            </p>
            <p className="text-[11px] font-mono text-muted-fg bg-surface-elevated py-1 px-2.5 rounded-lg border border-surface-border inline-block">
              {currentPath}
            </p>
          </div>
          <div className="pt-2">
            <button
              type="button"
              onClick={() => navigateTo('dashboard')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover transition-colors shadow-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>{t('admin.nav.dashboard')}</span>
            </button>
          </div>
        </div>
      );
    }

    // 2. Render standard 21 modules
    switch (activeView) {
      case 'dashboard':
        return <AdminDashboardView onNavigate={(view) => navigateTo(view)} />;
      case 'aiModels':
        return <AiModelsManagementView onTestModel={navigateToSandboxWithModel} />;
      case 'aiSandbox':
        return <AiSandboxView initialModelCode={sandboxModelCode} />;
      case 'keypool':
        return <KeyPoolMonitorView />;
      case 'plans':
        return <PlansManagementPage />;
      case 'gateways':
        return <PaymentGatewaysConfigView />;
      case 'orders':
        return <BankTransfersApprovalView />;
      case 'orderLogs':
        return <PaymentOrdersLogView />;
      case 'financial':
        return <FinancialManagementView />;
      case 'credits':
        return <AdminCreditsManagementView />;
      case 'users':
        return <UserDirectoryView />;
      case 'cmsBlog':
        return <CmsBlogManagementView />;
      case 'cmsPages':
        return <CustomPagesManagementView />;
      case 'cmsNavigation':
        return <NavigationMenuManagementView />;
      case 'settings':
        return <SystemSettingsView />;
      case 'themeStudio':
        return <ThemeStudioView />;
      case 'templateStudio':
        return <TemplateStudioView />;
      case 'brandStudio':
        return <BrandStudioView />;
      case 'seedAssets':
        return <SeedAssetsView />;
      case 'tokenAnalytics':
        return <TokenAnalyticsView onNavigate={(view) => navigateTo(view)} />;
      case 'auditLogs':
        return <AdminAuditLogsView />;
      default:
        return (
          <div className="p-12 text-center border border-dashed border-surface-border rounded-2xl bg-surface space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mx-auto">
              <Wrench className="w-6 h-6" />
            </div>
            <h3 className="text-base font-heading font-semibold text-foreground">
              {t(`admin.nav.${activeView}` as any)}
            </h3>
            <p className="text-xs text-muted-fg max-w-md mx-auto">
              This module is scheduled for implementation in upcoming Work Packages of Sprint 11.
            </p>
          </div>
        );
    }
  };

  return (
    <LayoutComponent activeView={activeView} onViewChange={setActiveView}>
      {renderView()}
    </LayoutComponent>
  );
};
